import * as Backbone from "backbone";
import * as Marionette from "backbone.marionette";
import Vue from 'vue';
import { Fragment } from 'vue-frag';
import axios from 'axios';
import * as Fuse from 'fuse.js';
import { groupBy, map, sortBy, values } from 'underscore';
import 'url-polyfill';
import * as Calus from 'calus';
import * as Cookies from 'js-cookie';

import { Options } from '../utils/decorators';
import FavoriteCount from './favorite-count';
import FavoriteButton from "../views/favorite-button";
import Favorites from "../favorites/favorites";
import moment from '../utils/moment';
import '../vendor/polyfills';


Vue.component('Fragment', Fragment);

Vue.mixin({
    methods: {
        lengthClass: (str, baseClass, shortLength = 10, longLength = 20) => {
            if (String(str).length >= longLength) {
                return `${baseClass}--long`;
            }

            if (String(str).length >= shortLength) {
                return `${baseClass}--short`;
            }

            return null;
        }
    },
});

const calendarConfig = (<any>window).calendarConfig;

const now = moment();

const buildQuery = filters => {
    const query = map(filters, function (facets:Array<any>, filter) {
        if (!facets.length) {
            return null;
        }

        return {
            '$or': facets.map(facet => {
                return {
                    [filter]: facet,
                }
            }),
        }
    });

    return query.filter(item => {
        return item !== null;
    });
}

const isDesktopView = () => {
    return window.matchMedia('(max-width: 920px)').matches === false;
}

const cloneDeep = source => {
    if (Array.isArray(source)) {
        return source.map(cloneDeep);
    }

    if (typeof source !== 'object') {
        return source;
    }

    // @ts-ignore
    return Object.fromEntries(
        // @ts-ignore
        Object.entries(source).map(([key, val]) => ([key, cloneDeep(val)]))
    )
}

const waitForElm = selector => {
    return new Promise(resolve => {
        if (document.querySelector(selector)) {
            return resolve(document.querySelector(selector));
        }

        const observer = new MutationObserver(mutations => {
            if (document.querySelector(selector)) {
                observer.disconnect();
                resolve(document.querySelector(selector));
            }
        });

        // If you get "parameter 1 is not of type 'Node'" error, see https://stackoverflow.com/a/77855838/492336
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    });
}

@Options({
    template: false,
    ui: {}
})

export default class CalendarWidgetView extends Marionette.View<Backbone.Model>{
    config?: object;
    element?: any;
    app?: Vue;

    constructor(options?: object, config?: object) {
        super(options);

        this.config = config || {};
        this.element = this.$el.get(0);
    }

    onRender() {
        this.app = new Vue({
            el: this.element,

            data() {
                return {
                    loading: true,
                    error: false,
                    seasons: {
                        current: [],
                        past: [],
                    },
                    currentSeason: null,
                    results: [],
                    filter: [],
                    canReset: false,
                    totalResults: 0,
                    allExpanded: false,
                }
            },

            created() {
                this.fuse = null;
                this.performances = null;
                this.stopUpdate = false;
                this.picker = null;
                this.disableUpdateCalendar = null;
                this.scrollTop = null;

                const customerConcertsString = Cookies.get('_bo_customer_concerts');

                this.customerConcerts = customerConcertsString ? decodeURIComponent(customerConcertsString).split(',') : [];
            },

            beforeMount() {
                const { Season } = this.getQuery();

                this.doChangeSeason(Season).then(() => {
                    window.addEventListener('popstate', () => {
                        this.applyQueryString();
                    });

                    this.$nextTick(() => {
                        // We need to initiate the global favourites count functionality
                        // which isn't a Vue thing, but inside a Vue component
                        // as such it doesn't recieve updates if favourites are updated
                        // so we have to get Vue to render it explictly
                        // and then Vue will recognise it, and keep an eye on it
                        const countElement = document.querySelector('[data-js-vue="favorite-count"]');

                        if (countElement) {
                            const count = new FavoriteCount({
                                el: countElement,
                            });

                            count.render();
                        }
                    });
                }).catch(() => {
                    this.error = true;
                    this.loading = false;
                });
            },

            mounted() {
                const seasons = {
                    current: [],
                    past: [],
                }

                for (const season of calendarConfig.seasons) {
                    if(moment(season.end_date).isSameOrAfter(now)) {
                        seasons.current.push(season);
                    } else {
                        seasons.past.push(season);
                    }
                }

                seasons.current = seasons.current.sort((a, b) => {
                    return a.site_id - b.site_id
                });

                this.seasons = seasons;

                document.addEventListener('mouseup', event => {
                    if (!(<HTMLElement>event.target).closest('.extra-message')) {
                        [].forEach.call(document.querySelectorAll('.extra-message__tooltip'), element => {
                            element.classList.remove('is-showing-tooltip');
                        });
                    }
                });

                window.addEventListener('resize', () => {
                    if (!this.picker) {
                        return;
                    }

                    this.picker.displayInColumn = !isDesktopView();
                });

                window.addEventListener('scroll', () => {
                    if (!this.picker || this.disableUpdateCalendar) {
                        return;
                    }

                    let monthHeaders = document.querySelectorAll('.hybrid-list__date-group h2');
                    let foundHeader:any = undefined;

                    for (let i = 0; i < monthHeaders.length; i++) {
                        let element = monthHeaders[i];

                        let position = window.getComputedStyle(element).top;
                        let top: any = element.getBoundingClientRect();

                        if (parseInt(position, 10) === top.y) {
                            foundHeader = element;
                        }
                    }

                    if (foundHeader !== undefined) {
                        let timestamp = parseInt(foundHeader.dataset.timestamp, 10);

                        let date = new Date(timestamp * 1000);

                        this.picker.removeSelectedStyle();

                        this.picker.currentDisplayedMonth = this.picker.currentDisplayedMonth.set({
                            day: date.getDate(),
                            month: date.getMonth() + 1, // Date month is 0-indexed, Luxon is 1-indexed
                            year: date.getFullYear(),
                        });

                        setTimeout(() => {
                            this.picker.addSelectedStyle(this.picker.selected);
                        }, 1);
                    }
                });
            },

            methods: {
                async refreshCalendar(season) {
                    const {
                        StartDate,
                        EndDate,
                    } = this.getQuery();

                    this.currentSeason = !season || season === 'null' || season === 'undefined' ? 'upcoming' : season;

                    this.loading = true;
                    this.error = false;

                    let uri = (<any>window).DJANGO.feedUrl;

                    if (this.currentSeason !== 'upcoming') {
                        const foundSeason = calendarConfig.seasons.find(item => {
                            return item.slug === this.currentSeason;
                        });

                        if (!foundSeason) {
                            this.error = true;
                            this.loading = false;

                            return;
                        }

                        uri += '/' + foundSeason.id;
                    }

                    if ((<any>window).DJANGO.isLive) {
                        uri += '/live';
                    } else {
                        uri += '/stage';
                    }

                    let startOfDay = null;
                    let endOfDay = null;

                    if (StartDate && EndDate) {
                        startOfDay = moment(new Date(+StartDate)).startOf('day').valueOf();
                        endOfDay = moment(new Date(+EndDate)).endOf('day').valueOf();
                    }

                    return axios.get(uri).then(response => {
                        const genreFacets = {};
                        const categoryFacets = {};
                        const timeFacets = {};
                        const venueFacets = {};

                        for (const site of calendarConfig.sites) {
                            venueFacets[site.name] = {
                                name: site.name,
                                checked: false,
                                disabled: false,
                                count: 0,
                            }
                        }

                        this.performances = [];

                        for (const performance of response.data) {

                            if (performance.hide_from_calendar) {
                                continue;
                            }

                            if (!performance.program || !performance.season) {
                                continue;
                            }

                            performance.start_time = moment(performance.start_time);
                            performance.start_time_iso = performance.start_time.toISOString();

                            if (startOfDay && endOfDay) {
                                const timestamp = performance.start_time.valueOf();

                                if (timestamp < startOfDay || timestamp > endOfDay) {
                                    continue;
                                }
                            }

                            if (performance.end_time) {
                                performance.end_time = moment(performance.end_time);
                            }

                            performance.sort = performance.start_time.format('YYYYMMDDHHmmss');
                            performance.group = performance.start_time.format('MMMM YYYY');

                            performance.is_going = this.customerConcerts.indexOf(performance.ticketing_system_id) !== -1;

                            for (const genre of performance.genres) {
                                if (genre.name in genreFacets === false) {
                                    genreFacets[genre.name] = {
                                        name: genre.name,
                                        checked: false,
                                        disabled: false,
                                        count: 1,
                                    }
                                } else {
                                    genreFacets[genre.name].count++;
                                }
                            }

                            for (const category of performance.categories) {
                                if (category.name in categoryFacets === false) {
                                    categoryFacets[category.name] = {
                                        name: category.name,
                                        checked: false,
                                        disabled: false,
                                        count: 1,
                                    }
                                } else {
                                    categoryFacets[category.name].count++;
                                }
                            }

                            if (performance.time.name in timeFacets === false) {
                                timeFacets[performance.time.name] = {
                                    name: performance.time.name,
                                    checked: false,
                                    disabled: false,
                                    count: 1,
                                }
                            } else {
                                timeFacets[performance.time.name].count++;
                            }

                            venueFacets[performance.site.name].count++;

                            if (performance.is_past) {
                                performance.absolute_url += '?force';
                            }

                            performance.show_venue = false;
                            performance.venue_class = null;

                            if (performance.venue) {
                                if (performance.venue.class === 'othervenue' || performance.venue.id !== calendarConfig.venue) {
                                    performance.show_venue = true;
                                }

                                if (performance.venue.id !== calendarConfig.venue) {
                                    performance.venue_class = performance.venue.class;
                                }
                            }

                            this.performances.push(performance);
                        }

                        this.performances = sortBy(this.performances, 'sort');

                        this.filters = [
                            {
                                name: 'Genre',
                                field: 'genres.name',
                                collapsible: true,
                                collapsed: false,
                                facets: values(genreFacets),
                            },
                            {
                                name: 'Category',
                                field: 'categories.name',
                                collapsible: true,
                                collapsed: true,
                                facets: values(categoryFacets),
                            },
                            {
                                name: 'Time',
                                field: 'time.name',
                                collapsible: false,
                                collapsed: false,
                                facets: values(timeFacets),
                            },
                            {
                                name: 'Venue',
                                field: 'site.name',
                                collapsible: true,
                                collapsed: true,
                                facets: values(venueFacets),
                            },
                        ];

                        // @ts-ignore
                        this.fuse = new Fuse(this.performances, {
                            threshold: 0.30,
                            useExtendedSearch: true,
                            includeScore: false,
                            shouldSort: false,
                            keys: [
                                'genres.name',
                                'categories.name',
                                'time.name',
                                'site.name',
                            ],
                        });

                        this.loading = false;
                    }).catch(() => {
                        this.error = true;
                        this.loading = false;
                    });
                },

                getQuery() {
                    const queries:any = {};

                    (<any>new URL(location.href).searchParams).forEach((value, key) => {
                        queries[key] = value;
                    });

                    return queries;
                },

                processResults() {
                    let results = this.performances;
                    let canReset = false;

                    const appliedFilters = {};

                    for (const filter of this.filters) {
                        appliedFilters[filter.field] = [];

                        for (const facet of filter.facets) {
                            if (facet.checked) {
                                appliedFilters[filter.field].push(facet.name);
                            }
                        }
                    }

                    const appliedQuery = buildQuery(appliedFilters);

                    if (appliedQuery.length) {
                        results = this.fuse.search({
                            '$and': appliedQuery,
                        }).map(result => {
                            return result.item;
                        });

                        canReset = true;
                    }

                    for (const filter of this.filters) {
                        for (const facet of filter.facets) {
                            const futureFilters = cloneDeep(appliedFilters);

                            futureFilters[filter.field].push(facet.name);

                            const potentialResults = this.fuse.search({
                                '$and': buildQuery(futureFilters),
                            });

                            facet.count = potentialResults.length;
                            facet.disabled = !potentialResults.length;
                        }
                    }

                    this.totalResults = results.length;

                    let groupedResults = groupBy(results, 'group');

                    groupedResults = <any>map(groupedResults, (items, key) => {
                        return {
                            name: key,
                            timestamp: (<any>items[0]).start_time.clone().startOf('month').format('X'),
                            items: items,
                        }
                    });

                    this.results = groupedResults;
                    this.canReset = canReset;

                    this.$nextTick(() => {
                        [].forEach.call(document.querySelectorAll('[data-js-vue="favorite-button"]'), element => {
                            if (!element.dataset.tessituraId || !element.dataset.timestamp) {
                                return;
                            }

                            const button = new FavoriteButton({
                                el: element,
                                favorites: Favorites.getFavorites(),
                                dontListen: true,
                            });

                            button.render();
                        });

                        this.updateCalendar().then(() => {
                            this.picker.setAvailable(results.map(performance => {
                                return performance.start_time_iso;
                            }));

                            if (results.length) {
                                const firstPerfDate = (<any>results[0]).start_time.clone();

                                this.picker.currentDisplayedMonth = this.picker.currentDisplayedMonth.set({
                                    month: firstPerfDate.format('MM'),
                                    year: firstPerfDate.format('YYYY'),
                                });
                            }
                        });
                    });
                },

                setDefaultQueryString() {
                    const searchParams = new URLSearchParams(window.location.search);

                    if (this.currentSeason === 'upcoming' && !searchParams.has('Venue')) {
                        const currentSite = calendarConfig.sites.find(site => {
                            return site.id === calendarConfig.site;
                        });

                        const queries = {};

                        for (const filter of this.filters) {
                            if (filter.name !== 'Venue') {
                                continue;
                            }

                            for (const facet of filter.facets) {
                                if (currentSite && currentSite.name === facet.name) {
                                    if (!(filter.name in queries)) {
                                        queries[filter.name] = [];
                                    }

                                    queries[filter.name].push(facet.name);
                                }
                            }
                        }

                        for (const filter of Object.keys(queries)) {
                            searchParams.set(filter, queries[filter].join(','));
                        }
                    }

                    if (this.currentSeason !== 'upcoming') {
                        searchParams.delete('Venue');
                    }

                    if (this.currentSeason) {
                        searchParams.set('Season', this.currentSeason);
                    }

                    const url = new URL(<any>window.location);

                    url.search = searchParams.toString();

                    window.history.pushState(null, '', url.toString());
                },

                applyQueryString() {
                    const searchParams = new URLSearchParams(window.location.search);

                    if (searchParams.has('Season') && searchParams.get('Season') !== this.currentSeason) {
                        this.refreshCalendar(searchParams.get('Season')).then(() => {
                            this.processResults();
                        });
                    } else {
                        this.filters = this.filters.map(filter => {
                            filter.facets = filter.facets.map(facet => {
                                let params = searchParams.get(filter.name);

                                facet.checked = (params) ? params.indexOf(facet.name) !== -1 : 0;

                                return facet;
                            });

                            return filter;
                        });

                        this.processResults();
                    }
                },

                updateQueryString() {
                    if (this.stopUpdate) {
                        return;
                    }

                    const searchParams = new URLSearchParams();

                    const queries = {};

                    for (const filter of this.filters) {
                        for (const facet of filter.facets) {
                            if (facet.checked) {
                                if (!(filter.name in queries)) {
                                    queries[filter.name] = [];
                                }

                                queries[filter.name].push(facet.name);
                            }
                        }
                    }

                    for (const filter of Object.keys(queries)) {
                        searchParams.set(filter, queries[filter].join(','));
                    }

                    if (this.currentSeason) {
                        searchParams.set('Season', this.currentSeason);
                    }

                    const url = new URL(<any>window.location);

                    url.search = searchParams.toString();

                    window.history.pushState(null, '', url.toString());
                },

                toggleProgramAndArtists(event) {
                    event.target.closest('.performance-card').classList.toggle('performance-info-open');
                },

                toggleAllProgramAndArtists() {
                    [].forEach.call(document.querySelectorAll('.performance-card'), card => {
                        if (this.allExpanded) {
                            card.classList.remove('performance-info-open');
                        } else {
                            card.classList.add('performance-info-open');
                        }
                    });

                    if (this.allExpanded) {
                        document.querySelector('.performance-info-toggles').classList.remove('expanded');
                    } else {
                        document.querySelector('.performance-info-toggles').classList.add('expanded');
                    }

                    this.allExpanded = !this.allExpanded;
                },

                showTooltip(event) {
                    const tooltip = event.currentTarget.closest('.extra-message').querySelector('.tooltip');

                    if (tooltip.classList.contains('is-showing-tooltip')) {
                        tooltip.classList.remove('is-showing-tooltip');
                    } else {
                        tooltip.classList.add('is-showing-tooltip');
                    }
                },

                showCalendarFilter(event) {
                    event.preventDefault();

                    const switcher = document.querySelector('.hybrid_filters');
                    const performanceFilter = document.querySelector('.performance-filter');

                    switcher.classList.add('calendar-filter');
                    switcher.classList.remove('facet-filter');

                    if (isDesktopView() === false) {
                        performanceFilter.classList.add('visible');
                        this.scrollTop = window.scrollY || document.body.scrollTop || document.documentElement.scrollTop;
                        document.querySelector('body').classList.add('calendar-fixed');
                    }
                },

                showCategoryFilter(event) {
                    event.preventDefault();

                    const switcher = document.querySelector('.hybrid_filters');
                    const performanceFilter = document.querySelector('.performance-filter');

                    switcher.classList.add('facet-filter');
                    switcher.classList.remove('calendar-filter');

                    if (isDesktopView() === false) {
                        performanceFilter.classList.add('visible');
                        this.scrollTop = window.scrollY || document.body.scrollTop || document.documentElement.scrollTop;
                        document.querySelector('body').classList.add('calendar-fixed');
                    }
                },

                collapseFilter(filterIndex, event) {
                    if (!this.filters[filterIndex].collapsible) {
                        return;
                    }

                    // Toggles the `facet-group__collapsed` class
                    event.target.closest('.facet-group').classList.toggle('facet-group__collapsed');

                    this.filters = this.filters.map((filter, alpha) => {
                        if (filterIndex === alpha) {
                            filter.collapsed = !filter.collapsed;
                        }

                        return filter;
                    });
                },

                updateFacet(filterIndex, facetIndex, event) {
                    this.filters = this.filters.map((filter, alpha) => {
                        filter.facets = filter.facets.map((facet, beta) => {
                            if (filterIndex === alpha && facetIndex === beta) {
                                facet.checked = event.target.checked;
                            }

                            return facet;
                        });

                        return filter;
                    });

                    this.processResults();
                    this.updateQueryString();
                },

                closeMobileOverlay() {
                    document.querySelector('.performance-filter').classList.remove('visible');
                    document.querySelector('body').classList.remove('calendar-fixed');

                    const scrollHeight = Math.max(
                        document.body.scrollHeight, document.documentElement.scrollHeight,
                        document.body.offsetHeight, document.documentElement.offsetHeight,
                        document.body.clientHeight, document.documentElement.clientHeight
                    );

                    let scrollTop = this.scrollTop;

                    if (scrollHeight < scrollTop) {
                        scrollTop = 0;
                    }

                    window.scrollTo(0, scrollTop);
                },

                clearFilters(event) {
                    event.preventDefault();

                    const currentSite = calendarConfig.sites.find(site => {
                        return site.id === calendarConfig.site;
                    });

                    this.filters = this.filters.map(filter => {
                        filter.facets = filter.facets.map(facet => {
                            if (this.currentSeason === 'upcoming' && filter.name === 'Venue' && currentSite && currentSite.name === facet.name) {
                                facet.checked = true;
                            } else {
                                facet.checked = false;
                            }

                            return facet;
                        });

                        return filter;
                    });

                    this.processResults();
                    this.updateQueryString();
                },

                changeSeason(event) {
                    if (event.target.value !== this.currentSeason) {
                        this.closeMobileOverlay();
                        this.doChangeSeason(event.target.value);
                    }
                },

                doChangeSeason(season) {
                    this.stopUpdate = true;

                    this.picker = null;

                    return this.refreshCalendar(season).then(() => {
                        this.setDefaultQueryString();

                        this.applyQueryString();

                        this.stopUpdate = false;
                    });
                },

                addDateToQueryString(day) {
                    let timestamp;

                    if (typeof day === 'string') {
                        timestamp = day;
                    } else {
                        timestamp = day.time.ts;
                    }

                    const query = this.getQuery();

                    const url = new URL(location.pathname, location.origin);

                    for (const prop in query) {
                        url.searchParams.set(prop, query[prop].split(','));
                    }

                    url.searchParams.set('Date', timestamp);

                    window.history.pushState(query, document.title, url.toString())
                },

                scrollToDate(date, smooth) {
                    let elem;

                    if (isDesktopView()) {
                        elem = document.querySelector('.hybrid-list__item--desktop [data-day="' + date.toISODate() + '"]');
                    } else {
                        elem = document.querySelector('.hybrid-list__item--mobile [data-day="' + date.toISODate() + '"]');
                    }

                    if (elem) {
                        elem.scrollIntoView({
                            behavior: smooth ? 'smooth' : 'auto',
                            block: 'start',
                        });
                    }
                },

                scrollToMonth(month) {
                    const timestamp = month.startOf('day').toFormat('X');
                    const monthHeaderEl = document.querySelector(`[data-timestamp="${timestamp}"]`);
                    const monthEl = document.querySelector(`[data-timestamp="${timestamp}"] ~ .${isDesktopView() ? 'hybrid-list__item--desktop' : 'hybrid-list__item--mobile'}`) as HTMLElement | null;

                    if (monthEl) {
                        const y = monthHeaderEl.getBoundingClientRect().height + monthEl.offsetTop;

                        this.disableUpdateCalendar = true;

                        window.scrollTo(0, y);

                        setTimeout(() => {
                            this.disableUpdateCalendar = false;
                        });
                    }
                },

                updateCalendar() {
                    return waitForElm('#datepicker').then(element => {
                        if (this.picker) {
                            return this.picker;
                        }

                        this.picker = new (<any>Calus)({
                            el: element,
                            onSelect: day => {
                                this.closeMobileOverlay();
                                this.addDateToQueryString(day);
                                this.scrollToDate(day.time, false);
                            },
                            onChangeMonth: (prev, current) => {
                                this.scrollToMonth(current);
                            },
                            weekStartsOnSunday: true,
                            displayInColumn: !isDesktopView(),
                            useDefaultTemplate: true,
                            displayFirstAvailableMonth: true,
                            timezone: 'America/Los_Angeles',
                        });

                        this.picker.weekStartOffset = -1;

                        const query = this.getQuery();

                        if (query.Date) {
                            const eventDate = new Date(parseInt(query.Date, 10));

                            // tried multiple scenarios but always ended up on (0,0) no matter what
                            // removing window.scrollTop from the code did not help
                            // cb for picker scrollMonth did not help either
                            if (eventDate) {
                                this.$nextTick(() => {
                                    let dateToSelect = null;

                                    if (isDesktopView()) {
                                        dateToSelect = document.querySelector(`.hybrid-list__item--desktop [data-day='${moment(eventDate).format('YYYY-MM-DD')}']`);
                                    } else {
                                        dateToSelect = document.querySelector(`.hybrid-list__item--mobile [data-day='${moment(eventDate).format('YYYY-MM-DD')}']`);
                                    }

                                    if (dateToSelect) {
                                        dateToSelect.scrollIntoView({
                                            behavior: 'auto',
                                            block: 'start',
                                        });
                                    }
                                });
                            }
                        } else {
                            window.scrollTo(0, 0);
                        }

                        return this.picker;
                    });
                },
            },
        });
    }
}



// WEBPACK FOOTER //
// ./@modules/views/calendar-widget-view.ts